import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { BaseCrud, FormSection, FormSectionColumn } from 'react/containers/layout/Crud';
import {
  TextField,
  DateField,
  IntegerField,
  LookupField
} from 'react/components/fields';

import { actionCreators as modalsActionCreators } from 'react/api/actions/modals';
import { actionCreators as eventHttpActionLogsActionCreators } from 'react/api/actions/eventHttpActionLogs';
import { generateLinkToDownloadCSV, getResourceTo } from 'react/utils/utils';

function EventLogHttpActionsCrud(props) {
  const dispatch = useDispatch();
  const eventHttpActionLogs = useSelector((state) => state.eventHttpActionLogs.get('data', []));

  const nameSpace = useMemo(() => 'EventLogHttpAction', []);

  const getParsedCollectionData = (data) => {
    const arrayOfData = data.map((item) => new App.Models.EventLogHttpAction(item));
    const parsedData = new App.Collections.EventLogHttpActions();

    arrayOfData.forEach((item) => {
      parsedData.add(item);
    });

    return parsedData;
  };

  const getActualPage = () => {
    const paginationFirst = App.Forms[nameSpace].pagination.first;

    return paginationFirst ? paginationFirst / 51 : 0;
  };

  const addDataToGrid = (data) => {
    const columns = App.Forms[nameSpace].table.getColumns();
    const parsedData = getParsedCollectionData(data);

    if (!data.length) {
      Monet.Grid.addDataToGrid(nameSpace, columns, parsedData, true);
      Monet.Grid.Utils.showGridNoResult(nameSpace);

      return;
    }

    Monet.Grid.Utils.hideGridNoResult(nameSpace);

    const eventHttpActionLogsData = App.Forms[nameSpace].dataProvider.data;
    const page = getActualPage();

    if (eventHttpActionLogsData.length === 0 || page === 0) {
      Monet.Grid.addDataToGrid(nameSpace, columns, parsedData, true);
    } else {
      Monet.Grid.appendDataToGridAfterPagination(nameSpace, columns, parsedData);
    }
  };

  const setRequestDateRangeFilters = (filter, activeFilters) => {
    const filterValue = filter.getValue();
    const { ini: startDate, fin: endDate } = filterValue;

    if (startDate) {
      activeFilters.startEventDate = startDate;
    }

    if (endDate) {
      activeFilters.endEventDate = endDate;
    }
  };

  const hasAValidFilterValue = (value) =>
    (typeof(value) === 'string' && String(value)?.length) ||
    (typeof(value) === 'number' && value >= 0);

  const getActiveFilters = () => {
    const fields = App.Forms[nameSpace]?.filterBuilder?.fields || {};
    const activeFilters = {};

    Object.keys(fields).forEach((key) => {
      const filter = fields[key];
      const { name } = filter;
      const { model } = filter.getFullValue();
      const filterValue = filter.getValue();
      const specialsFieldsNames = [
        'eventDate_Range',
        'event',
        'route',
        'equipment',
      ];

      if(name === 'eventDate_Range') {
        setRequestDateRangeFilters(filter, activeFilters);
      }

      if(name === 'event' && model?.id) {
        activeFilters.eventId = model.id;
      }

      if(name === 'route' && model?.id) {
        activeFilters.routeId = model.id;
      }

      if(name === 'equipment' && model?.id) {
        activeFilters.equipmentId = model.id;
      }

      if (
        !specialsFieldsNames.includes(name) &&
        hasAValidFilterValue(filterValue)
      ) {
        activeFilters[key] = filterValue;
      }
    });

    return activeFilters;
  };

  const setCustomSaveList = () => {
    const {
      actions: { exportFormats },
      fields: formFields
    } = App.Forms[nameSpace];

    App.Forms[nameSpace].actions.exportFormats.setScreenName(nameSpace);
    App.Forms[nameSpace].actions.exportFormats.saveFieldList = () => {
      exportFormats.handlerToggleLoadingInBtn();

      const fields = exportFormats.getFieldsSelecteds();

      const date = new Date();
      const fileName = `EventActionsLogs_${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}.csv`;

      const parsedFields = fields
        .map((field) => field.replace(/"/g, ''))
        .map((field) => (field === 'event.description' ? 'event' : field))
        .map((field) => (field === 'route.key' ? 'route' : field))
        .map((field) => (field === 'equipment.key' ? 'equipment' : field));

      let data = '';

      data += Object.values(parsedFields)
        .map((field) => {
          const fieldData = formFields[field];
          return fieldData?.translatedLabel || getResourceTo(fieldData?.label);
        })
        .join(',');

      data += '\n';

      const formattedData = App.Forms[nameSpace].dataProvider.data
        .map((item) => {
          const itemModel = item.model.attributes;

          return parsedFields
            .map((field) => {
              const value = itemModel[field];

              switch (true) {
                case field === 'event':
                  return itemModel.eventDescription;
                case field === 'route':
                  return itemModel.routeKey;
                case field === 'equipment':
                  return itemModel.equipmentKey;
                case [null, undefined].includes(value):
                  return '';
                default:
                  return String(value).replace(/,/g, '.');
              }
            })
            .join(',');
        })
        .join('\n');

      data += formattedData;

      generateLinkToDownloadCSV({
        fileName,
        data,
        callback: () => {
          exportFormats.inProgress = false;
          exportFormats.handlerToggleLoadingInBtn();
          exportFormats.togglePopUp();
        }
      });
    };
  };

  const setCustomExecuteFilter = () => {
    App.Forms[nameSpace].filterBuilder.customExecuteFilter = () => {
      Monet.Grid.Utils.hideGridNoResult(nameSpace);

      const page = getActualPage();

      if (page === 0)
        App.Forms[nameSpace].pagination.blocked = false;

      const activeFilters = getActiveFilters();

      const filtersPayload = {
        page: Math.ceil(page),
        size: 51
      };

      Object.assign(filtersPayload, activeFilters);

      dispatch(eventHttpActionLogsActionCreators.fetchEventHttpActionLogs({ filtersPayload }));
    };
  };

  const setCustomFilterBuilderFields = () => {
    let fieldsList = App.Forms[nameSpace]?.filterBuilder?.fieldList;

    delete fieldsList.eventDescription;
    delete fieldsList.action;
    delete fieldsList.statusCode_Range;
    delete fieldsList.status;
    delete fieldsList.url;

    fieldsList = Object.keys(fieldsList);

    App.Forms[nameSpace]?.filterBuilder?.setFieldsToDisplayOnFilterBuilder(fieldsList);
    App.Forms[nameSpace]?.filterBuilder?.render();
  };

  const setCustomCustomizeColumns = () => {
    App.Forms[nameSpace].actions.customizecolumns.setCallBacks({
      afterSaveList: () => {
        const router = App.Objects[nameSpace];

        router.list();
      }
    });
  };

  const setSortBy = () => {
    App.Forms[nameSpace].table.sortBy = 'client';
  };

  const getParsedJsonParameter = (jsonParameter) => JSON.parse(jsonParameter);

  const setCustomColumnsFormatter = () => {
    const tableColumns = App.Forms[nameSpace]?.table?.getColumns() || [];

    const updatedTableColumns = tableColumns.map((columnItem) => {
      switch (columnItem.field) {
        case 'status':
          return {
            ...columnItem,
            width: 86,
            formatter: (row, cell, value, column, dataContext) => {
              const { model: { attributes } } = dataContext;
              const { status } = attributes;

              const statusText = {
                ERROR: {
                  class: 'error',
                  title: getResourceTo('eventLogHttpAction.status.error')
                },
                FAILED: {
                  class: 'error',
                  title: getResourceTo('eventLogHttpAction.status.error')
                },
                SUCCEEDED: {
                  class: 'success',
                  title: getResourceTo('eventLogHttpAction.status.success')
                },
                OK: {
                  class: 'success',
                  title: getResourceTo('eventLogHttpAction.status.success')
                },
                EXECUTING: {
                  class: 'executing',
                  title: getResourceTo('eventLogHttpAction.status.pending')
                },
                PENDING: {
                  class: 'pending',
                  title: getResourceTo('eventLogHttpAction.status.pending')
                }
              };

              return `<div class='httpStatus-${statusText[status].class}' title='${statusText[status].title}'></div>`;
            }
          };
        case 'url': {
          return {
            ...columnItem,
            width: 300,
            formatter: (row, cell, value, column, dataContext) => {
              const { model: { attributes } } = dataContext;
              const { url } = getParsedJsonParameter(attributes.jsonParameter);

              return url;
            }
          };
        }
        case 'event.description': {
          return {
            ...columnItem,
            width: 300,
            formatter: (row, cell, value, column, dataContext) => {
              const { model: { attributes } } = dataContext;
              const { eventDescription } = attributes;

              return eventDescription;
            }
          };
        }
        case 'route.key': {
          return {
            ...columnItem,
            width: 300,
            formatter: (row, cell, value, column, dataContext) => {
              const { model: { attributes } } = dataContext;
              const { routeKey } = attributes;

              return routeKey;
            }
          };
        }
        case 'equipment.key': {
          return {
            ...columnItem,
            width: 300,
            formatter: (row, cell, value, column, dataContext) => {
              const { model: { attributes } } = dataContext;
              const { equipmentKey } = attributes;

              return equipmentKey;
            }
          };
        }
        case 'statusCode': {
          return {
            ...columnItem,
            formatter: (row, cell, value, column, dataContext) => {
              const { model: { attributes } } = dataContext;
              let { statusCode, executionInfo } = attributes;
              
              if(executionInfo) {
                statusCode = executionInfo.responseInfo.statusCode;
              }

              return statusCode !== 0 ? statusCode : '';
            }
          };
        }
        default:
          return columnItem;
      }
    });

    updatedTableColumns.length && App.Forms[nameSpace].table.setColumns(updatedTableColumns);
  };

  const setCustomOnClickRow = () => {
    const grid = App.Forms[nameSpace].table;

    const onClickRow = (e, args) => {
      const item = args.grid.getData().data[args.row];

      if (!item.model.attributes) return;

      const {
        jsonParameter,
        eventDate,
        requestBody,
        status,
        actionExecuteResultMessage,
        executionInfo,
        statusCode
      } = item.model.attributes;

      const { url } = getParsedJsonParameter(jsonParameter);

      dispatch(modalsActionCreators.openModal({
        modalKey: 'logDetails',
        title: 'eventLogHttpAction.modal.title',
        data: {
          url,
          date: Monet.Format.Date.formatToLocale(eventDate),
          httpStatus: executionInfo ? executionInfo.responseInfo.statusCode : statusCode,
          requestBody: requestBody,
          requestType: 'POST',
          responseMessage: actionExecuteResultMessage,
          isPending: ['EXECUTING', 'PENDING'].includes(status)
        },
      }));
    };

    grid.onClick.unsubscribe(onClickRow);
    grid.onClick.subscribe(onClickRow);
  };

  const setCustomMonetUILoadingContainerToAppend = () => {
    const tabElement = document.getElementById(nameSpace);

    App.Forms[nameSpace]?.filterBuilder?.loading?.setContainerToAppend(tabElement);
    App.Forms[nameSpace].table.getContainerNode = () => tabElement;
  };

  const beforeList = () => {
    setCustomSaveList();
    setCustomCustomizeColumns();
    setSortBy();
    setCustomFilterBuilderFields();
    setCustomColumnsFormatter();
    setCustomOnClickRow();
    setCustomExecuteFilter();
    setCustomMonetUILoadingContainerToAppend();
  };

  useEffect(() => {
    if (eventHttpActionLogs?.size >= 0) {
      const data = eventHttpActionLogs.toJS();

      addDataToGrid(data);
    }
  }, [eventHttpActionLogs]);

  return (
    <>
      <BaseCrud
        isGrid
        entity="EventLogHttpAction"
        customOptions={{
          columnCheckBox: {
            show: false,
          },
          rowHeight: 32,
          sanitizer: null
        }}
        updateTitle={props.updateTitle}
        gridCallbacks={{
          beforeList
        }}
      >
        <FormSection title={getResourceTo('EventLog')} className="generalSection">
          <FormSectionColumn>
            <TextField
              label={getResourceTo('eventLogHttpAction.field.status')}
              name="status"
            />
            <LookupField
              label={getResourceTo('eventLogHttpAction.field.event')}
              name="event"
              lookupEntity="Event"
              lookupEntityColumn="description"
            />
            <TextField
              label={getResourceTo('eventLogHttpAction.field.action')}
              name="action"
            />
            <TextField
              label={getResourceTo('event.http.url')}
              name="url"
            />
            <IntegerField
              label={getResourceTo('integrationLog.field.responseStatus')}
              name="statusCode"
            />
            <DateField
              label={getResourceTo('date')}
              name="eventDate"
              type="DATE_TIME"
            />
            <LookupField
              label={getResourceTo('eventLogHttpAction.field.route')}
              name="route"
              lookupEntity="Route"
              lookupEntityColumn="key"
            />
            <LookupField
              label={getResourceTo('eventLogHttpAction.field.equipment')}
              name="equipment"
              lookupEntity="Equipment"
              lookupEntityColumn="key"
            />
          </FormSectionColumn>
        </FormSection>
      </BaseCrud>
    </>
  );
}

EventLogHttpActionsCrud.propTypes = {
  updateTitle: PropTypes.func.isRequired
};

export default EventLogHttpActionsCrud;
